import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Storage as ServerIcon,
  CreditCard as PaymentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Security & GDPR - adiLessons";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Understand how adiLessons handles student data security, GDPR compliance, and PCI-compliant online payments.');
  }, []);

  const protocols = [
    {
      icon: <LockIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'UK GDPR Compliance',
      description: 'We adhere strictly to UK Data Protection Laws. All student contact information, driving syllabus grades, and booking logs are stored securely. Students and instructors retain full rights to request data access or deletion.'
    },
    {
      icon: <ServerIcon sx={{ fontSize: 32, color: '#25D366' }} />,
      title: 'Secure Hosting & Backups',
      description: 'Data is hosted in industry-leading UK and EU datacenters with automated daily backups. Traffic between your browser and our API is encrypted using secure TLS protocols.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 32, color: '#ffb300' }} />,
      title: 'PCI-Compliant Payments',
      description: 'Online student fee transactions are processed directly by PCI-DSS Level 1 compliant gateways (PayPal and Stripe). Your financial details and payment card information never touch our servers.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#2b3154',
          zIndex: 1100
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', height: 72 }}>
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2b3154', 
                  letterSpacing: '-0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif'
                }}
              >
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
            bgcolor: 'white',
          }}
        >
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: '#e8f0fe', mb: 2 }}>
              <SecurityIcon sx={{ fontSize: 40, color: '#1967D2' }} />
            </Box>
            <Typography variant="h3" fontWeight="900" sx={{ mb: 2, color: '#0f162a', fontFamily: '"Outfit", sans-serif' }}>
              Security & UK GDPR Compliance
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '18px', lineHeight: 1.6 }}>
              How we secure your driving school diaries, student details, and billing logs.
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8, fontSize: '16px', mb: 4 }}>
            At <strong>adiLessons</strong>, data security and compliance with the UK GDPR are fundamental. We are committed to maintaining the confidentiality, integrity, and availability of all data processed within our system.
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8, fontSize: '16px', mb: 4 }}>
            adiLessons is operated under the brand name <strong>adiLessons</strong>. All data processing agreements and service structures are governed under the UK Data Protection Act (DPA 2018) and UK GDPR laws. Our Data Protection Officer (DPO) can be reached at <span style={{ fontWeight: 'bold' }}>support@adilesson.co.uk</span> for any inquiries, Subject Access Requests (SARs), or data deletion requests.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f162a', mb: 3 }}>
            Technical Security Measures
          </Typography>

          <Grid container spacing={4} sx={{ mb: 4 }}>
            {protocols.map((protocol, index) => (
              <Grid size={{ xs: 12 }} key={index}>
                <Box display="flex" alignItems="flex-start" gap={3}>
                  <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: 3, bgcolor: '#f1f5f9' }}>
                    {protocol.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#0f162a', mb: 1 }}>
                      {protocol.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {protocol.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f162a', mb: 3 }}>
            Compliance & Operational Policies
          </Typography>

          <Box display="flex" flexDirection="column" gap={3} sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.7 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                4. Third-Party Subprocessors
              </Typography>
              <Typography variant="body2">
                We work with industry-standard subprocessors to run the scheduling platform securely:
                <br />• <strong>Amazon Web Services (AWS)</strong>: Core infrastructure provider, cloud hosting in UK datacenters.
                <br />• <strong>WhapiCell</strong>: Gateways used to dispatch automatic WhatsApp confirmations and reminders.
                <br />• <strong>Stripe / PayPal</strong>: PCI-DSS compliant billing services. Raw credit card data never touches our database.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                5. Incident Response & Breach Notification
              </Typography>
              <Typography variant="body2">
                We maintain an active monitoring and incident response plan. In the unlikely event of a verified database security breach, we are legally committed to notifying all affected users and the Information Commissioner's Office (ICO) within 72 hours under UK DPA 2018 guidelines.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                6. Data Retention & Account Deletion
              </Typography>
              <Typography variant="body2">
                Instructors can download their active pupil records, lesson schedules, and invoice logs at any time. When an account is terminated, all personal student phone numbers, diaries, and logs are completely purged from our active systems and backups within 30 days.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a', mt: 'auto' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved. adiLessons is operated under the brand name adiLessons. Contact: support@adilesson.co.uk.
        </Typography>
      </Box>
    </Box>
  );
};

export default SecurityPage;
